/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import { observer } from "mobx-react";
import styled, { css } from "styled-components";

import { ThemePalette } from "@src/components/Theme"; // (ThemeProps was for exactWidth)
import { humanise, statusInfo } from "@src/components/ui/Lists/ListTable";
import runningImage from "./images/running.svg";
import cancellingImage from "./images/cancelling.svg";

/*
 * 2026-10-07: the labels are StatePill's (ListTable's STATUS_INFO), in sentence case and spelled
 * "Cancelled", so a status reads the same in the execution header and the task list as in the
 * lists and detail headers. Was this upper-case map ("CANCELED", "UNSCHEDULABLE", ...):
 */
// const LABEL_MAP: { [status: string]: string } = {
//   CANCELED_FOR_DEBUGGING: "DEBUG",
//   FORCE_CANCELED: "CANCELED",
//   STRANDED_AFTER_DEADLOCK: "DEADLOCKED",
//   CANCELED_AFTER_COMPLETION: "CANCELED",
//   CANCELLING_AFTER_COMPLETION: "CANCELLING",
//   FAILED_TO_SCHEDULE: "UNSCHEDULABLE",
//   FAILED_TO_CANCEL: "CANCELED",
//   AWAITING_MINION_ALLOCATIONS: "AWAITING AGENTS",
//   ERROR_ALLOCATING_MINIONS: "AGENTS ERROR",
//   // Minion Pool statuses
//   VALIDATING_INPUTS: "VALIDATING",
//   ALLOCATING_SHARED_RESOURCES: "ALLOCATING",
//   ALLOCATING_MACHINES: "ALLOCATING",
//   DEALLOCATING_MACHINES: "DEALLOCATING",
//   DEALLOCATING_SHARED_RESOURCES: "DEALLOCATING",
//   IN_MAINTENANCE: "MAINTENANCE",
//   RESCALING: "SCALING",
//   IN_USE: "IN USE",
//   // Minion Machine power statuses
//   // On / Off, Starting… / Stopping… (UI spec 8.4); were POWERING OFF / POWERING ON / POWERED ON / POWERED OFF.
//   POWERING_OFF: "Stopping…",
//   POWERING_ON: "Starting…",
//   POWERED_ON: "On",
//   POWERED_OFF: "Off",
//   POWER_ERROR: "ERROR",
// };

// const POWER_STATES = ["POWERING_OFF", "POWERING_ON", "POWERED_ON", "POWERED_OFF"]; (see the Wrapper)

const statuses = (status: any) => {
  switch (status) {
    case "COMPLETED":
    case "ALLOCATED": // Minion Pool status
    case "POWERED_ON": // Minion Machine status
    case "AVAILABLE": // Minion Pool status
      return css`
        background: var(--t9-success-50);
        color: var(--t9-success-700);
        border-color: transparent;
      `;
    case "FAILED_TO_SCHEDULE":
    case "FAILED_TO_CANCEL":
    case "ERROR":
    case "ERROR_ALLOCATING_MINIONS":
    case "POWER_ERROR": // Minion Machine power status
      return css`
        background: var(--t9-danger-50);
        color: var(--t9-danger-700);
        border-color: transparent;
      `;
    case "CANCELED":
    case "CANCELED_FOR_DEBUGGING":
    case "CANCELED_AFTER_COMPLETION":
    case "FORCE_CANCELED":
    case "WARNING":
      return css`
        background: var(--t9-warning-50);
        color: var(--t9-warning-700);
        border-color: transparent;
      `;
    case "PAUSED":
      return css`
        background: var(--t9-bg-surface);
        color: ${ThemePalette.primary};
        border-color: ${ThemePalette.primary};
      `;
    case "STARTING":
    case "RUNNING":
    case "PENDING":
    case "AWAITING_MINION_ALLOCATIONS":
    case "INITIALIZING": // Minion Pool status
    case "ALLOCATING": // Minion Pool status
    case "RECONFIGURING": // Minion Pool status
    case "VALIDATING_INPUTS": // Minion Pool status
    case "ALLOCATING_SHARED_RESOURCES": // Minion Pool status
    case "ALLOCATING_MACHINES": // Minion Pool status
    case "SCALING": // Minion Pool status
    case "RESCALING": // Minion Pool status
    case "DEPLOYING": // Minion Pool status
    case "IN_USE": // Minion Pool status
    case "HEALTHCHECKING": // Minion Machine status
    case "POWERING_ON": // Minion Machine power status
      return css`
        background: url("${runningImage}");
        animation: bgMotion 1s infinite linear;
        color: white;
        border-color: transparent;
        @keyframes bgMotion {
          0% {
            background-position: -12px 0;
          }
          100% {
            background-position: 0 0;
          }
        }
      `;

    case "CANCELLING":
    case "UNINITIALIZING":
    case "DEALLOCATING": // Minion Machine status
    case "POWERING_OFF": // Minion Machine power status
    case "CANCELLING_AFTER_COMPLETION":
    case "IN_MAINTENANCE": // Minion Pool status
    case "DEALLOCATING_MACHINES": // Minion Pool status
    case "DEALLOCATING_SHARED_RESOURCES": // Minion Pool status
      return css`
        background: url("${cancellingImage}");
        animation: bgMotion 1s infinite linear;
        color: ${ThemePalette.black};
        border-color: transparent;
        @keyframes bgMotion {
          0% {
            background-position: -12px 0;
          }
          100% {
            background-position: 0 0;
          }
        }
      `;
    case "STRANDED_AFTER_DEADLOCK":
    case "DEADLOCKED":
      return css`
        background: var(--t9-tooltip-bg);
        color: var(--t9-tooltip-text);
        border-color: transparent;
      `;
    case "UNSCHEDULED":
    case "UNKNOWN": // Minion Pool/Machine status
    case "UNINITIALIZED": // Minion Pool/Machine status
    case "DEALLOCATED": // Minion Pool status
    case "INITIALIZED": // Minion Pool status
    case "POWERED_OFF": // Minion Machine status
      return css`
        background: ${ThemePalette.grayscale[1]};
        color: ${ThemePalette.grayscale[4]};
        border-color: transparent;
      `;
    case "INFO":
    case "SCHEDULED":
    case "UNEXECUTED":
      return null;
    default:
      return null;
  }
};

const primaryColors = css`
  color: ${ThemePalette.primary};
  border-color: ${ThemePalette.primary};
  background: var(--t9-bg-surface);
`;
const alertColors = css`
  color: ${ThemePalette.alert};
  border-color: ${ThemePalette.alert};
  background: var(--t9-bg-surface);
`;
const secondaryColors = css`
  color: ${ThemePalette.grayscale[4]};
  border-color: ${ThemePalette.grayscale[1]};
  background: ${ThemePalette.grayscale[1]};
`;
const getInfoStatusColor = (props: any) => {
  if (props.alert) {
    return alertColors;
  }

  if (props.secondary) {
    return secondaryColors;
  }

  return primaryColors;
};
const Wrapper = styled.div<any>`
  /* Was exactly 84 / 104 px wide with upper-case 10 px text; sentence-case labels ("Could not
     schedule") may be longer, so the width is a minimum now. */
  /* was: ThemeProps.exactWidth(small ? 84 : 104 px) */
  min-width: ${(props: any) => (props.small ? 84 : 104)}px;
  box-sizing: border-box;
  padding: 0 8px;
  white-space: nowrap;
  height: 20px;
  line-height: 18px;
  border: 1px solid;
  font-size: 11px;
  font-weight: 600;
  /* letter-spacing: 0.04em; (for the upper-case labels) */
  text-align: center;
  border-radius: 9999px;
  ${(props: any) => statuses(props.status)}
  ${(props: any) => (props.status === "INFO" ? getInfoStatusColor(props) : "")}
  /* Were "text-transform: uppercase" and, for the power states ("On" / "Off", UI spec 8.4), an
     exception back to normal case; every label is sentence case now.
  (POWER_STATES: "text-transform: none; font-size: 11px;") */
  overflow: hidden;
`;

type Props = {
  status: string | null;
  label?: string;
  primary?: boolean;
  secondary?: boolean;
  alert?: boolean;
  small?: boolean;
  style?: React.CSSProperties;
};
@observer
class StatusPill extends React.Component<Props> {
  static defaultProps = {
    status: "INFO",
  };

  render() {
    const { status } = this.props;
    // Was: let label = this.props.label || this.props.status; label = LABEL_MAP[label || ""] || label;
    const label =
      this.props.label ||
      (status && status !== "INFO" ? statusInfo(status).label : humanise(status || "INFO"));

    return (
      <Wrapper
        {...this.props}
        status={status}
        primary={this.props.primary}
        secondary={this.props.secondary}
        alert={this.props.alert}
        small={this.props.small}
      >
        {label}
      </Wrapper>
    );
  }
}

export default StatusPill;
